import type { GlobalPreferences } from "@/lib/global-preferences";

export type TranslationKey = keyof typeof messages.ja;

const messages = {
  ja: {
    all: "すべて",
    workspaceWide: "ワークスペース全体",
    askAi: "AIに質問",
    searchWorkspace: "ワークスペースを検索",
    noSearchResults: "検索結果はありません",
    markAllRead: "すべて既読にする",
    noNotifications: "新しい通知はありません",
    calendarTasks: "予定・タスク",
    aiHistory: "AI履歴",
    searchMode: "検索モード",
    searchOrAskAi: "検索・AIに質問",
    admin: "管理",
    adminMenu: "管理者メニュー",
    unread: "未読",
    mentions: "メンション",
    attendance: "参加記録",
    fileUpdates: "ファイル更新",
    survey: "アンケート",
    permissions: "権限",
    aiActions: "AI操作",
    syncAdmin: "同期管理",
    globalLabel: "全体",
    globalBadge: "全体",
    switchWorkspace: "ワークスペースを切り替える",
    mainNav: "主要ナビゲーション",
    mobileNav: "モバイル主要ナビゲーション",
    notificationSettings: "通知設定",
    notificationChannels: "通知チャンネル",
    notificationEventTypes: "イベント種別",
    inApp: "アプリ内",
    browserPush: "ブラウザPush",
    emailDigest: "メールDigest",
    pushNotifications: "Push通知",
    pushSubscribe: "Push通知を登録",
    send: "送信",
    read: "既読",
    testNotification: "テスト通知",
    onboarding: "初期設定",
    emailVerification: "メールアドレス認証",
    sendCode: "認証コードを送信",
    verify: "認証する",
    workspaceCreate: "ワークスペースを作成",
    workspaceId: "ワークスペースID（任意）",
    autoAssign: "空欄の場合は自動で割り当てます",
    createContinue: "作成して続ける",
    later: "後で設定",
    notifications: "通知設定",
    saveContinue: "保存して続ける",
    ready: "準備完了",
    start: "開始する",
    login: "ログイン",
    accountCreate: "アカウント作成",
    password: "パスワード",
    email: "メールアドレス",
    displayName: "表示名",
    home: "ホーム",
    chat: "チャット",
    calendar: "予定",
    files: "ファイル",
    docs: "資料",
    myPage: "マイページ",
    workspace: "ワークスペース",
    members: "メンバー",
    featureList: "機能一覧",
    chatDetails: "チャンネル詳細",
    membersOnline: "オンライン",
    membersOffline: "オフライン",
    createSurvey: "アンケートを作成",
    noActiveSurveys: "進行中のアンケートはありません",
    noEvents: "予定はありません",
    noPins: "ピン留めされたメッセージはありません",
    noThreads: "参加中のスレッドはありません",
    noMedia: "共有メディアはありません",
    noFiles: "共有ファイルはありません",
    noLinks: "共有リンクはありません",
    close: "閉じる",
    cancel: "キャンセル",
    search: "検索",
    settings: "設定",
    integrations: "連携",
    disconnect: "接続解除",
    import: "インポート",
    resume: "続きから再開",
    entity: "対象",
    entityOverview: "概要",
    entityActivity: "活動履歴",
    entityFeatures: "機能",
    entityPermissions: "権限",
    entityLink: "関連付け",
    entityMerge: "統合",
    entityUnmerge: "統合を解除",
    entityContextConflict: "共通情報の競合",
    entityFeatureConflict: "同種機能の競合",
    preserveBoth: "両方を保持",
    canonicalOnly: "canonical側だけ保持",
    save: "保存",
    delete: "削除",
    retry: "再試行",
    empty: "表示する項目はありません",
    error: "エラー",
  },
  en: {
    all: "All",
    workspaceWide: "Workspace-wide",
    askAi: "Ask AI",
    searchWorkspace: "Search across the workspace",
    noSearchResults: "No results",
    markAllRead: "Mark all read",
    noNotifications: "No new notifications",
    calendarTasks: "Calendar / Tasks",
    aiHistory: "AI history",
    searchMode: "Search mode",
    searchOrAskAi: "Search or ask AI",
    admin: "Admin",
    adminMenu: "Admin menu",
    unread: "Unread",
    mentions: "Mentions",
    attendance: "Attendance",
    fileUpdates: "File updates",
    survey: "Survey",
    permissions: "Permissions",
    aiActions: "AI actions",
    syncAdmin: "Sync admin",
    globalLabel: "Global",
    globalBadge: "ALL",
    switchWorkspace: "Switch workspace",
    mainNav: "Main navigation",
    mobileNav: "Mobile navigation",
    notificationSettings: "Notification settings",
    notificationChannels: "Notification channels",
    notificationEventTypes: "Event types",
    inApp: "In-app",
    browserPush: "Browser push",
    emailDigest: "Email digest",
    pushNotifications: "Push notifications",
    pushSubscribe: "Subscribe to push",
    send: "Send",
    read: "Read",
    testNotification: "Test notification",
    onboarding: "Onboarding",
    emailVerification: "Email verification",
    sendCode: "Send verification code",
    verify: "Verify",
    workspaceCreate: "Create workspace",
    workspaceId: "Workspace ID (optional)",
    autoAssign: "Leave blank to assign automatically",
    createContinue: "Create and continue",
    later: "Set up later",
    notifications: "Notifications",
    saveContinue: "Save and continue",
    ready: "Ready",
    start: "Start",
    login: "Log in",
    accountCreate: "Create account",
    password: "Password",
    email: "Email address",
    displayName: "Display name",
    home: "Home",
    chat: "Chat",
    calendar: "Calendar",
    files: "Files",
    docs: "Documents",
    myPage: "My Page",
    workspace: "Workspace",
    members: "Members",
    featureList: "Feature list",
    chatDetails: "Channel details",
    membersOnline: "Online",
    membersOffline: "Offline",
    createSurvey: "Create survey",
    noActiveSurveys: "No active surveys",
    noEvents: "No events",
    noPins: "No pinned messages",
    noThreads: "No joined threads",
    noMedia: "No shared media",
    noFiles: "No shared files",
    noLinks: "No shared links",
    close: "Close",
    cancel: "Cancel",
    search: "Search",
    settings: "Settings",
    integrations: "Integrations",
    disconnect: "Disconnect",
    import: "Import",
    resume: "Resume",
    entity: "Entity",
    entityOverview: "Overview",
    entityActivity: "Activity",
    entityFeatures: "Features",
    entityPermissions: "Permissions",
    entityLink: "Link",
    entityMerge: "Merge",
    entityUnmerge: "Unmerge",
    entityContextConflict: "Context conflict",
    entityFeatureConflict: "Feature conflict",
    preserveBoth: "Keep both",
    canonicalOnly: "Keep canonical only",
    save: "Save",
    delete: "Delete",
    retry: "Retry",
    empty: "Nothing to show",
    error: "Error",
  },
} as const;

const eventTypeLabels: Record<string, { ja: string; en: string }> = {
  entity_created: { ja: "対象を作成", en: "Entity created" },
  feature_attached: { ja: "機能を追加", en: "Feature attached" },
  entity_linked: { ja: "対象を関連付け", en: "Entities linked" },
  entity_merged: { ja: "対象を統合", en: "Entities merged" },
  entity_unmerged: { ja: "統合を解除", en: "Entities unmerged" },
  lifecycle_active: { ja: "利用中へ変更", en: "Marked active" },
  lifecycle_completed: { ja: "完了へ変更", en: "Marked completed" },
  lifecycle_archived: { ja: "アーカイブへ変更", en: "Archived" },
  message_created: { ja: "メッセージを投稿", en: "Message posted" },
  calendar_created: { ja: "予定を作成", en: "Calendar event created" },
  calendar_updated: { ja: "予定を更新", en: "Calendar event updated" },
  calendar_deleted: { ja: "予定を削除", en: "Calendar event deleted" },
  poll_created: { ja: "アンケートを作成", en: "Survey created" },
  poll_responded: { ja: "アンケートに回答", en: "Survey answered" },
  document_created: { ja: "資料を作成", en: "Document created" },
  document_updated: { ja: "資料を更新", en: "Document updated" },
  file_created: { ja: "ファイルを追加", en: "File added" },
  file_updated: { ja: "ファイルを更新", en: "File updated" },
  file_deleted: { ja: "ファイルを削除", en: "File deleted" },
  file_version_uploaded: { ja: "ファイルの版を追加", en: "File version uploaded" },
  file_version_created: { ja: "ファイルの版を作成", en: "File version created" },
  file_version_restored: { ja: "ファイルの版を復元", en: "File version restored" },
  file_commented: { ja: "ファイルへコメント", en: "File commented" },
  task_created: { ja: "タスクを作成", en: "Task created" },
  task_updated: { ja: "タスクを更新", en: "Task updated" },
  task_deleted: { ja: "タスクを削除", en: "Task deleted" },
  task_commented: { ja: "タスクへコメント", en: "Task commented" },
  task_time_entry: { ja: "工数を記録", en: "Time entry recorded" },
  recording_started: { ja: "録音を開始", en: "Recording started" },
  recording_stopped: { ja: "録音を停止", en: "Recording stopped" },
  recording_uploaded: { ja: "録音を保存", en: "Recording uploaded" },
  unread: { ja: "未読", en: "Unread" },
  mention: { ja: "メンション", en: "Mention" },
  dm: { ja: "ダイレクトメッセージ", en: "Direct message" },
  task: { ja: "タスク", en: "Task" },
  calendar: { ja: "予定", en: "Calendar" },
  doc: { ja: "資料", en: "Document" },
  entity: { ja: "対象", en: "Entity" },
};

const settingsLabels: Record<string, string> = {
  "アカウント情報": "Account information",
  "ログイン端末": "Login devices",
  "ログインとセキュリティ": "Login devices",
  "ログイン方法": "Login methods",
  "認証連携": "Login methods",
  "表示・言語": "Display & language",
  "全体ホーム": "Workspace-wide home",
  "所属ワークスペース": "Workspaces",
  "招待から参加": "Join from invitation",
  "新規ワークスペース作成": "Create workspace",
  "自分のプロフィール": "My profile",
  "ホーム": "Home",
  "チャット": "Chat",
  "予定・記録": "Schedule & records",
  "ファイル": "Files",
  "通知": "Notifications",
  "個人連携": "Personal integrations",
  "所属情報・退会": "Membership & leave",
  "概要": "Overview",
  "メンバー・組織": "Members & groups",
  "招待": "Invitations",
  "ロール・権限": "Roles & permissions",
  "チャンネル": "Channels",
  "ホーム設定": "Home settings",
  "予定・記録・タスク設定": "Schedule, records & tasks",
  "ファイル設定": "File settings",
  "外部連携": "External integrations",
  "データ・同期": "Data & sync",
  "監査ログ": "Audit log",
  "Danger Zone": "Danger zone",
  "アカウント": "Account",
  "自分": "Me",
  "ワークスペース": "Workspace",
};

export function translate(key: TranslationKey, locale: GlobalPreferences["locale"] = "ja") {
  return messages[locale][key] ?? messages.ja[key];
}

export function translateEventType(eventType: string | null | undefined, locale: GlobalPreferences["locale"] = "ja") {
  const label = eventTypeLabels[String(eventType ?? "")];
  return label?.[locale] ?? String(eventType ?? "");
}

export function translateSettingText(value: string, locale: GlobalPreferences["locale"] = "ja") {
  if (locale !== "en") return value;
  return settingsLabels[value] ?? value;
}
